import { useRef, useState } from "react";
import type { AuthUser } from "../types";
import { TwoFactorSection } from "./TwoFactorSection";

interface Props {
  user: AuthUser;
  onClose: () => void;
  onDeleted: () => void;
  onRefreshUser: () => void;
}

/**
 * Lê um arquivo de imagem e devolve um data URL já REDIMENSIONADO (lado máx. em
 * `max` px, JPEG/PNG) - mantém o avatar pequeno (bem abaixo do limite do backend)
 * e evita subir foto de câmera de 5 MB.
 */
async function fileToAvatarDataUrl(file: File, max = 256): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("falha ao ler o arquivo"));
    reader.readAsDataURL(file);
  });
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error("imagem inválida"));
    el.src = dataUrl;
  });
  const scale = Math.min(1, max / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return dataUrl;
  ctx.drawImage(img, 0, 0, w, h);
  // PNG preserva transparência; demais viram JPEG (menor)
  const mime = file.type === "image/png" ? "image/png" : "image/jpeg";
  return canvas.toDataURL(mime, 0.85);
}

/**
 * Meus dados (epico 10/LGPD) - export e exclusão self-service. Qualquer
 * pessoa logada acessa isto, não é uma tela de admin.
 */
export function MyDataPanel({ user, onClose, onDeleted, onRefreshUser }: Props) {
  const [confirmEmail, setConfirmEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // --- perfil (nome + foto) ---
  const [name, setName] = useState(user.name ?? "");
  const [avatar, setAvatar] = useState<string | null>(user.avatarDataUrl ?? null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSaved, setProfileSaved] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const profileDirty = name.trim() !== (user.name ?? "") || (avatar ?? null) !== (user.avatarDataUrl ?? null);

  const onPickAvatar = async (file: File | undefined) => {
    if (!file) return;
    setProfileError(null);
    try {
      setAvatar(await fileToAvatarDataUrl(file));
      setProfileSaved(false);
    } catch {
      setProfileError("não foi possível processar essa imagem");
    }
  };

  const saveProfile = async () => {
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      setProfileError("o nome precisa ter ao menos 2 caracteres");
      return;
    }
    setSavingProfile(true);
    setProfileError(null);
    setProfileSaved(false);
    try {
      const r = await fetch("/api/auth/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: trimmed, avatarDataUrl: avatar }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setProfileError(j.error ?? "falha ao salvar o perfil");
        return;
      }
      setProfileSaved(true);
      onRefreshUser();
    } finally {
      setSavingProfile(false);
    }
  };

  const deleteAccount = async () => {
    if (confirmEmail.trim() !== user.email) {
      setError("o e-mail digitado não confere");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await fetch("/api/me", {
        method: "DELETE",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ confirm: confirmEmail.trim() }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(j.error ?? "Falha ao excluir os dados.");
        return;
      }
      onDeleted();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="about-page" role="dialog" aria-label="Meus dados">
      <header className="about-header">
        <h1>Meus dados</h1>
        <button className="about-close" onClick={onClose} aria-label="Fechar">
          ×
        </button>
      </header>

      <section className="about-section">
        <h2>Perfil</h2>
        <p className="admin-user-meta" style={{ marginBottom: 12 }}>
          Seu nome e sua foto aparecem no app. O nome é compartilhado com os outros apps da plataforma;
          a foto é só deste app.
        </p>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <div
              className="user-avatar"
              aria-hidden
              style={{
                width: 72,
                height: 72,
                fontSize: 28,
                backgroundImage: avatar ? `url(${avatar})` : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              {!avatar && (name || user.email).charAt(0).toUpperCase()}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              style={{ display: "none" }}
              onChange={(e) => void onPickAvatar(e.target.files?.[0])}
            />
            <div style={{ display: "flex", gap: 6 }}>
              <button className="btn-approve" type="button" onClick={() => fileInputRef.current?.click()}>
                Trocar foto
              </button>
              {avatar && (
                <button
                  className="btn-reject"
                  type="button"
                  onClick={() => {
                    setAvatar(null);
                    setProfileSaved(false);
                  }}
                >
                  Remover
                </button>
              )}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 240, display: "flex", flexDirection: "column", gap: 8 }}>
            <label className="field">
              <span>Nome</span>
              <input
                type="text"
                value={name}
                maxLength={80}
                onChange={(e) => {
                  setName(e.target.value);
                  setProfileSaved(false);
                }}
                placeholder="Seu nome"
              />
            </label>
            {profileError && <div className="auth-error">{profileError}</div>}
            {profileSaved && !profileDirty && (
              <div className="admin-user-meta" style={{ color: "var(--success)" }}>
                Perfil atualizado.
              </div>
            )}
            <div>
              <button
                className="btn-approve"
                type="button"
                disabled={savingProfile || !profileDirty}
                onClick={() => void saveProfile()}
              >
                {savingProfile ? "Salvando…" : "Salvar perfil"}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="about-section">
        <h2>Exportar meus dados</h2>
        <p className="admin-user-meta" style={{ marginBottom: 10 }}>
          Baixa um JSON com todas as suas conversas e seu histórico de atividade neste app (só os seus
          dados, não os da organização toda).
        </p>
        <a className="btn-approve" style={{ display: "inline-block", textDecoration: "none" }} href="/api/me/export" download>
          Baixar meus dados (.json)
        </a>
      </section>

      <TwoFactorSection user={user} onRefreshUser={onRefreshUser} />

      <section className="about-section">
        <h2>Excluir meus dados</h2>
        <p className="admin-user-meta" style={{ marginBottom: 10 }}>
          Remove permanentemente seu acesso a este app, suas conversas e seu histórico de atividade.
          <strong> Isso não pode ser desfeito.</strong> Sua identidade compartilhada (e-mail/senha, usada
          também por outros apps da plataforma) não é afetada.
        </p>
        {!showConfirm ? (
          <button className="btn-reject" type="button" onClick={() => setShowConfirm(true)}>
            Excluir meus dados
          </button>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 360 }}>
            <label className="field">
              <span>Digite seu e-mail ({user.email}) para confirmar</span>
              <input
                type="email"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder={user.email}
                spellCheck={false}
              />
            </label>
            {error && <div className="auth-error">{error}</div>}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-reject" disabled={busy} onClick={() => void deleteAccount()} type="button">
                {busy ? "Excluindo…" : "Confirmar exclusão definitiva"}
              </button>
              <button className="btn-approve" disabled={busy} onClick={() => setShowConfirm(false)} type="button">
                Cancelar
              </button>
            </div>
          </div>
        )}
      </section>

      <footer className="about-footer">
        <button className="about-back" onClick={onClose}>
          Voltar à conversa
        </button>
      </footer>
    </div>
  );
}
